// Business Metrics Tracker
// Tracks key business metrics for analytics and monitoring

class BusinessMetricsTracker {
  constructor() {
    this.metrics = {
      sessions: 0,
      totalTimeInApp: 0,
      calculations: 0,
      errors: 0,
      conversions: 0,
      pageViews: 0,
      uniqueUsers: new Set(),
      averageSessionTime: 0,
      calculationSuccessRate: 0,
      errorRate: 0,
      conversionRate: 0
    };
    
    this.sessionStartTime = Date.now();
    this.isInitialized = true;
  }

  // Track time spent in app
  trackTimeInApp(minutes) {
    if (typeof minutes !== 'number' || minutes < 0) return;
    
    this.metrics.totalTimeInApp += minutes;
    this.metrics.sessions += 1;
    this.metrics.averageSessionTime = this.metrics.totalTimeInApp / this.metrics.sessions;
    
    // Store in localStorage for persistence
    this.saveMetrics();
  }

  // Track calculation events
  trackCalculation(success = true) {
    this.metrics.calculations += 1;
    if (!success) {
      this.metrics.errors += 1;
    }
    
    this.metrics.calculationSuccessRate = 
      ((this.metrics.calculations - this.metrics.errors) / this.metrics.calculations) * 100;
    
    this.metrics.errorRate = (this.metrics.errors / this.metrics.calculations) * 100;
    
    this.saveMetrics();
  }

  // Track page views
  trackPageView(page) {
    this.metrics.pageViews += 1;
    this.saveMetrics();
  }

  // Track conversions (subscriptions, etc.)
  trackConversion(type = 'unknown') {
    this.metrics.conversions += 1;
    this.metrics.conversionRate = (this.metrics.conversions / this.metrics.sessions) * 100;
    this.saveMetrics();
  }

  // Track calculator usage
  trackCalculatorUsage(calculatorType) {
    this.metrics.calculations += 1;
    this.saveMetrics();
  }

  // Track unique users
  trackUser(userId) {
    if (userId) {
      this.metrics.uniqueUsers.add(userId);
    }
  }

  // Get current metrics
  getMetrics() {
    return {
      ...this.metrics,
      uniqueUserCount: this.metrics.uniqueUsers.size
    };
  }

  // Reset metrics
  resetMetrics() {
    this.metrics = {
      sessions: 0,
      totalTimeInApp: 0,
      calculations: 0,
      errors: 0,
      conversions: 0,
      pageViews: 0,
      uniqueUsers: new Set(),
      averageSessionTime: 0,
      calculationSuccessRate: 0,
      errorRate: 0,
      conversionRate: 0
    };
    this.saveMetrics();
  }

  // Save metrics to localStorage
  saveMetrics() {
    try {
      const metricsToSave = {
        ...this.metrics,
        uniqueUsers: Array.from(this.metrics.uniqueUsers)
      };
      localStorage.setItem('businessMetrics', JSON.stringify(metricsToSave));
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }

  // Load metrics from localStorage
  loadMetrics() {
    try {
      const saved = localStorage.getItem('businessMetrics');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.metrics = {
          ...parsed,
          uniqueUsers: new Set(parsed.uniqueUsers || [])
        };
      }
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }

  // Initialize tracker
  initialize() {
    this.loadMetrics();
    this.isInitialized = true;
  }
}

// Create singleton instance
const businessMetricsTracker = new BusinessMetricsTracker();
businessMetricsTracker.initialize();

export default businessMetricsTracker;